import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Info, ChevronRight, ChevronLeft, CheckCircle2,
  Globe, Lock, MapPin, AlertCircle, AlertTriangle,
  Layers, Calendar, ShieldCheck, Zap, Save, X
} from "lucide-react";
import { EndpointUrlInput } from "../components/EndpointUrlInput";
import { MetadataSection } from "../components/MetadataSection";
import { DataThemesSection } from "../components/DataThemesSection";
import { ScopeSection } from "../components/ScopeSection";
import { CategoryDropdown } from "../components/CategoryDropdown";
import { CollectionCategoryPicker } from "../components/CollectionCategoryPicker";
import { JobEventLog } from "../components/JobEventLog";
import { AuthorizationServerField } from "../components/AuthorizationServerField";
import { CatalogName, DialogHeader, DIALOG_TONES, Modal, ResultOverlay } from "../components/Modal";
import { CARD, HomeButton, PageNav } from "../components/PageChrome";
import { accessRights, creatorTypes, euDataThemes } from "../constants";
import { useAuth } from "../context/AuthContext";
import { API } from "../api";
import {
  autoCategoriesOf, categoriesFromCollections, collectionCategoriesPayload, collectionMissingCategory,
} from "../lib/collections";
import { apiErrorMessage } from "../lib/format";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const InfoTooltip = ({ text }) => (
  <div className="group relative inline-block ml-auto cursor-help">
    <div className="p-1.5 bg-green-100/50 text-green-700 rounded-full hover:bg-green-600 hover:text-white transition-all">
      <Info size={16} />
    </div>
    <div className="absolute bottom-full right-0 mb-3 hidden group-hover:block w-72 p-4 bg-slate-900 text-white text-xs leading-relaxed rounded-2xl shadow-2xl z-50 border border-slate-700">
      {text}
      <div className="absolute top-full right-3 border-8 border-transparent border-t-slate-900" />
    </div>
  </div>
);

const FieldWrapper = ({ label, tooltip, error, children, icon: Icon }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between px-1">
      <label className={`flex items-center gap-2 text-[16px] font-semibold uppercase tracking-widest ${error ? "text-red-500" : "text-gray-900"}`}>
        {Icon && <Icon size={14} className={error ? "text-red-500" : "text-green-500"} />}
        {label}
      </label>
      <InfoTooltip text={tooltip} />
    </div>
    {children}
    <AnimatePresence>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-red-500 text-xs font-bold italic mt-2 flex items-center gap-1 ml-1"
        >
          <AlertCircle size={12} /> {error}
        </motion.p>
      )}
    </AnimatePresence>
  </div>
);

function ReviewItem({ label, value, isLongText }) {
  return (
    <div className={`${isLongText ? "col-span-2" : ""} py-2`}>
      <p className="text-[10px] font-semibold text-green-500/80 uppercase tracking-widest mb-1">{label}</p>
      <p className={`text-sm font-medium leading-relaxed ${!value ? 'text-red-400 italic' : 'text-slate-200'}`}>
        {value || "Not Provided"}
      </p>
    </div>
  );
}

const INITIAL_FORM_DATA = {
  endpointURL: "",
  modified: "",
  geometry: "",
  category: [],
  datasetScope: "",
  dataThemes: [],
  access_right: "",
  authorizationServer: "",
  containsSyntheticData: "",
  isAccessibleWithoutPayment: "",
  isAccessibleWithoutRegistration: "",
  hasCreatorType: "",
  collectionCategories: {},
};

const INITIAL_META = {
  endpointDescription: "",
  title: "",
  description: "",
  provider: "",
  keywords: [],
  license: "",
  termsOfService: "",
  language: "",
};

const STEPS = ["Identity", "Access", "Scope", "Review"];

const FIELD_LABELS = {
  endpointURL: "Dataset Endpoint URL",
  endpointDescription: "OpenAPI Specification URL",
  title: "Dataset Title",
  description: "Description",
  license: "License Type URL",
  provider: "Data Provider",
  termsOfService: "License Attribution",
  keywords: "Keywords",
  category: "NEED Categories",
  collectionCategories: "Collection Categories",
  modified: "Last Date Modified",
  access_right: "Access Governance",
  authorizationServer: "Authorization Server",
  containsSyntheticData: "Contains Synthetic Content",
  isAccessibleWithoutPayment: "Cost-Free Access",
  isAccessibleWithoutRegistration: "Registration-Free Access",
  hasCreatorType: "Creator Type",
  dataThemes: "EUROPA Data Themes",
  datasetScope: "Geographic Scope",
  location: "Location",
};

// Adds every per-collection category to the dataset-level categories.
function withCollectionCategories(prev, map) {
  return { ...prev, collectionCategories: map,
           category: [...new Set([...(prev.category || []), ...categoriesFromCollections(map)])] };
}

// Registration drafts live in the shared Redis.
async function readDraft() {
  try {
    const res = await API.get("/api/register/draft");
    const draft = res.data?.draft;
    return draft && typeof draft === "object" ? draft : null;
  } catch {
    return null;
  }
}

async function writeDraft(payload) {
  try {
    await API.put("/api/register/draft", payload);
  } catch {
    /* a lost autosave is retried on the next change */
  }
}

async function clearDraft() {
  try {
    await API.delete("/api/register/draft");
  } catch {
    /* ignore */
  }
}

export default function Register({ onBack }) {
  const { logout, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  // Which organisation the datasource is registered for; only a member of several has to choose.
  const userOrgs = user?.orgs || [];
  const [ownerOrgId, setOwnerOrgId] = useState("");
  const needsOrgChoice = userOrgs.length > 1;
  // Set when Submit is pressed without a choice, to flag the picker in red.
  const [orgChoiceMissing, setOrgChoiceMissing] = useState(false);
  const orgPickerRef = useRef(null);
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [meta, setMeta] = useState(INITIAL_META);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [activeJobId, setActiveJobId] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");

  const [resumeOffered, setResumeOffered] = useState(false);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [showLeavePrompt, setShowLeavePrompt] = useState(false);
  // Draft loaded from Redis on mount.
  const [serverDraft, setServerDraft] = useState(null);

  // Offer to resume an existing draft once on mount.
  useEffect(() => {
    if (resumeOffered) return;
    let cancelled = false;
    (async () => {
      const draft = await readDraft();
      if (cancelled) return;
      setServerDraft(draft);
      if (draft && (draft.formData?.endpointURL || draft.meta?.title)) {
        setShowResumePrompt(true);
      }
      setResumeOffered(true);
    })();
    return () => { cancelled = true; };
  }, [resumeOffered]);

  const isDirty = (
    formData.endpointURL.trim() !== "" ||
    !!meta.title?.trim() ||
    formData.category.length > 0 ||
    formData.dataThemes.length > 0 ||
    Object.keys(formData.collectionCategories || {}).length > 0 ||
    selectedLocations.length > 0 ||
    selectedStates.length > 0 ||
    currentStep > 0
  );

  const saveDraft = () =>
    writeDraft({ ts: Date.now(), currentStep, formData, meta, selectedLocations, selectedStates });

  // Autosave to Redis.
  useEffect(() => {
    if (!resumeOffered || isSuccess || !isDirty) return;
    const t = setTimeout(saveDraft, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeOffered, isSuccess, isDirty, currentStep, formData, meta, selectedLocations, selectedStates]);

  const resumeDraft = () => {
    const draft = serverDraft;
    if (draft) {
      if (draft.formData) setFormData({ ...INITIAL_FORM_DATA, ...draft.formData });
      if (draft.meta) setMeta({ ...INITIAL_META, ...draft.meta });
      if (Array.isArray(draft.selectedLocations)) setSelectedLocations(draft.selectedLocations);
      if (Array.isArray(draft.selectedStates)) setSelectedStates(draft.selectedStates);
      if (typeof draft.currentStep === "number") setCurrentStep(draft.currentStep);
    }
    setShowResumePrompt(false);
  };

  const discardDraft = () => {
    clearDraft();
    setServerDraft(null);
    setShowResumePrompt(false);
  };

  const requestBack = () => {
    if (!onBack) return;
    if (isSuccess || !isDirty) {
      clearDraft();
      onBack();
      return;
    }
    setShowLeavePrompt(true);
  };

  const leaveSaveAndExit = () => {
    saveDraft();
    setShowLeavePrompt(false);
    onBack && onBack();
  };

  const leaveDiscardAndExit = () => {
    clearDraft();
    setShowLeavePrompt(false);
    onBack && onBack();
  };

  const clearError = (field) => setErrors(prev => {
    if (!prev[field]) return prev;
    const { [field]: _removed, ...rest } = prev;
    return rest;
  });

  // The datasource-level categories include every per-collection category.
  const handleCollectionCategoriesChange = (map) => {
    setFormData(prev => withCollectionCategories(prev, map));
    clearError("category");
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    clearError(field === "datasetScope" && value === "state" ? "geometry" : field);
  };

  useEffect(() => {
    const filled = {
      endpointURL: !!formData.endpointURL?.trim(),
      endpointDescription: !!meta.endpointDescription?.trim(),
      title: !!meta.title?.trim(),
      description: !!meta.description?.trim(),
      license: !!meta.license?.trim(),
      provider: !!meta.provider?.trim(),
      termsOfService: !!meta.termsOfService?.trim(),
      keywords: Array.isArray(meta.keywords) && meta.keywords.length > 0,
      category: Array.isArray(formData.category) && formData.category.length > 0,
      dataThemes: Array.isArray(formData.dataThemes) && formData.dataThemes.length > 0,
      datasetScope: !!formData.datasetScope,
      location:
        formData.datasetScope === "custom"
          ? selectedStates.length > 0
          : selectedLocations.some((l) => l.type === "state"),
    };
    setErrors(prev => {
      let changed = false;
      const next = { ...prev };
      for (const [field, isFilled] of Object.entries(filled)) {
        if (isFilled && next[field]) {
          delete next[field];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [formData, meta, selectedLocations, selectedStates]);

  useEffect(() => {
    if (!formData.endpointURL.trim()) {
      setMeta(INITIAL_META);
      setCollections([]);
      return;
    }
    const timer = setTimeout(async () => {
      setMetaLoading(true);
      setCollectionsLoading(true);
      setErrors(prev => ({ ...prev, metaFetchError: null }));

      API.get(`/api/metadata/collections?url=${encodeURIComponent(formData.endpointURL)}`)
        .then((res) => {
          const cs = Array.isArray(res.data?.collections) ? res.data.collections : [];
          setCollections(cs);
          setFormData(prev => {
            // Keep picks for collections that still exist; auto categories are always present.
            const next = {};
            for (const c of cs) {
              const existing = prev.collectionCategories?.[c.id];
              next[c.id] = [...new Set([...autoCategoriesOf(c), ...(Array.isArray(existing) ? existing : [])])];
            }
            return withCollectionCategories(prev, next);
          });
        })
        .catch((e) => {
          console.warn("Collections discovery failed:", e?.response?.data?.detail || e.message);
          setCollections([]);
        })
        .finally(() => setCollectionsLoading(false));

      try {
        const res = await API.get(`/api/metadata?url=${encodeURIComponent(formData.endpointURL)}`);
        const fetchedCats = Array.isArray(res.data.category) ? res.data.category : [];
        setMeta({
          ...INITIAL_META,
          ...res.data,
          keywords: Array.isArray(res.data.keywords) ? res.data.keywords : [],
          fetchedCategories: fetchedCats,
        });

        const existingCategory = Array.isArray(formData.category) ? formData.category : [];
        const mergedCategories = [...new Set([...existingCategory, ...fetchedCats])];
        updateField('category', mergedCategories);

        setErrors(prev => ({ ...prev, endpointURL: null, metaFetchError: null }));
      } catch (e) {
        const msg = e.response?.data?.detail || "Could not fetch metadata.";
        setErrors(prev => ({ ...prev, metaFetchError: msg }));
      } finally {
        setMetaLoading(false);
      }
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.endpointURL]);

  const validateCurrentPage = () => {
    const newErrors = {};
    const need = (field, filled, suffix = "is required") => {
      if (!filled) newErrors[field] = `${FIELD_LABELS[field]} ${suffix}`;
    };
    if (currentStep === 0) {
      need("endpointURL", formData.endpointURL?.trim());
      for (const f of ["endpointDescription", "title", "description", "license", "provider"]) need(f, meta[f]?.trim());
      // License Attribution Text is optional.
      need("keywords", meta.keywords?.length, "are required (add at least one)");
      need("category", formData.category.length);
      const missingCollection = collectionMissingCategory(collections, formData.collectionCategories);
      if (missingCollection) {
        newErrors.collectionCategories = `Pick at least one category for collection "${missingCollection.id}"`;
      }
    }
    if (currentStep === 1) {
      for (const f of ["modified", "access_right", "containsSyntheticData", "isAccessibleWithoutPayment",
                       "isAccessibleWithoutRegistration", "hasCreatorType"]) need(f, formData[f]);
      if (formData.access_right === "RESTRICTED") need("authorizationServer", formData.authorizationServer?.trim());
    }
    if (currentStep === 2) {
      need("dataThemes", formData.dataThemes.length);
      need("datasetScope", formData.datasetScope);
      if (formData.datasetScope === "custom" && selectedStates.length === 0) {
        newErrors.location = "Select at least one state for the custom geographic scope";
      } else if (formData.datasetScope === "state" && !selectedLocations.some((l) => l.type === "state")) {
        // Administrative scope needs at least a state-level pick.
        newErrors.location = "Pick a state (and optionally a district or municipality)";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const missingFieldNames = () =>
    Object.keys(errors)
      .filter((k) => k !== "metaFetchError" && errors[k])
      .map((k) => FIELD_LABELS[k] || k);

  const handleNext = () => { if (validateCurrentPage()) setCurrentStep(s => s + 1); };
  const handleBack = () => setCurrentStep(s => s - 1);
  const handleSubmit = async () => {
    if (needsOrgChoice && !ownerOrgId) {
      setOrgChoiceMissing(true);
      orgPickerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setLoading(true);

    const data = new FormData();
    data.append("title", meta.title || "");
    data.append("description", meta.description || "");
    data.append("providerName", meta.provider || "");
    if (needsOrgChoice) data.append("orgId", ownerOrgId);
    data.append("language", meta.language || "de");
    data.append("license", meta.license || "");
    if (meta.termsOfService) data.append("termsOfService", meta.termsOfService);
    data.append("openapi_url", meta.endpointDescription || "");
    data.append("keywords", JSON.stringify(meta.keywords || []));
    data.append("category", JSON.stringify(formData.category || []));
    data.append("dataThemes", JSON.stringify(formData.dataThemes || []));
    data.append("endpointURL", formData.endpointURL);
    data.append("modified", formData.modified);
    data.append("access_right", formData.access_right);
    if (formData.access_right === "RESTRICTED") data.append("authorizationServer", formData.authorizationServer || "");
    data.append("hasCreatorType", formData.hasCreatorType);
    data.append("containsSyntheticData", formData.containsSyntheticData === 'true' || formData.containsSyntheticData === true);
    data.append("isAccessibleWithoutPayment", formData.isAccessibleWithoutPayment === 'true' || formData.isAccessibleWithoutPayment === true);
    data.append("isAccessibleWithoutRegistration", formData.isAccessibleWithoutRegistration === 'true' || formData.isAccessibleWithoutRegistration === true);

    data.append("collectionCategories",
                JSON.stringify(collectionCategoriesPayload(collections, formData.collectionCategories)));

    let finalGeometry = null;
    let finalState = "";
    if (formData.datasetScope === "custom") {
      finalGeometry = formData.geometry || null;
      if (selectedStates.length > 0) {
        finalState = selectedStates[0].code;
        data.append("multiStates", JSON.stringify(selectedStates.map(s => s.code)));
      }
    } else {
      const activeLoc = selectedLocations[selectedLocations.length - 1];
      finalGeometry = activeLoc?.geometry || null;

      const stateObj = selectedLocations.find(l => l.type === 'state');
      finalState = stateObj?.code || "";
    }

    data.append("geometry", finalGeometry);
    data.append("state", finalState);

    try {
      const response = await API.post("/api/convert", data);
      if (response.data?.job_id) setActiveJobId(response.data.job_id);
    } catch (err) {
      setFailureMessage(apiErrorMessage(err, "Submission failed before the backend could start the job.", FIELD_LABELS));
    } finally {
      setLoading(false);
    }
  };

  const handleJobTerminal = ({ status, error }) => {
    if (status === "completed") {
      setTimeout(() => {
        clearDraft();
        setIsSuccess(true);
        setLoading(false);
      }, 600);
    } else if (status === "failed") {
      setLoading(false);
      setFailureMessage(error || "The backend job reported a failure while registering the datasource.");
    }
  };

  const handleSuccessRestart = () => {
    setActiveJobId(null);
    setIsSuccess(false);
    if (onBack) onBack();
    else window.location.reload();
  };

  const closeFailure = () => {
    setFailureMessage("");
    setActiveJobId(null);
  };

  return (
    <div className="min-h-screen bg-slate-200 text-slate-900 font-sans">
      <PageNav label="REGISTRATION" user={user} onLogout={logout}>
        <div className="hidden md:flex items-center gap-8">
          {STEPS.map((title, i) => (
            <div key={i} className={`flex items-center gap-3 transition-all ${currentStep === i ? "opacity-100 scale-105" : "opacity-30"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${currentStep >= i ? "bg-green-600 text-white" : "bg-slate-200 text-slate-500"}`}>
                {currentStep > i ? <CheckCircle2 size={14} /> : i + 1}
              </div>
              <span className="text-[10px] font-black uppercase tracking-[0.2em]">{title}</span>
            </div>
          ))}
        </div>
      </PageNav>
      <main className="pt-28 pb-20 flex justify-center px-6">
        <div className="w-full max-w-4xl">
          <div className={`relative ${CARD} p-10 md:p-20`}>
            <div className="-mt-4 md:-mt-10 mb-8 flex items-center justify-between gap-4">
              {onBack ? <HomeButton onClick={requestBack} /> : <span />}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-100 text-green-700 rounded-full text-[11px] font-black uppercase tracking-widest">
                <Zap size={12} fill="currentColor" /> Progress: {((currentStep + 1) / STEPS.length) * 100}%
              </div>
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4, ease: "circOut" }}>

                {currentStep === 0 && (
                  <div className="space-y-14">
                    <div className="mb-10">
                      <h2 className="text-xl font-black uppercase tracking-[0.3em] text-green-600 mb-2">
                        Getting Started
                      </h2>
                      <p className="text-gray-800 text-lg font-medium leading-relaxed">
                        Provide the details below to register your Dataset in the NEED-Catalog.
                      </p>
                    </div>
                    <FieldWrapper icon={Globe} label="Dataset Endpoint URL" tooltip="The URL where the dataset or API lives." error={errors.endpointURL || errors.metaFetchError}>
                      <EndpointUrlInput value={formData.endpointURL} onChange={(e) => updateField('endpointURL', e.target.value)} error={errors.endpointURL || errors.metaFetchError} loading={metaLoading} />
                    </FieldWrapper>
                    <MetadataSection meta={meta} setMeta={setMeta} loading={metaLoading} errors={errors} />
                    <FieldWrapper icon={Layers} label="DATASOURCE - NEED Categories" tooltip="Categorise according to NEED Metadata taxonomy categories." error={errors.category}>
                      <CategoryDropdown value={formData.category} fetchedCategories={meta.fetchedCategories} onChange={(vals) => updateField('category', vals)} />
                    </FieldWrapper>
                    {(collectionsLoading || collections.length > 0) && (
                      <FieldWrapper
                        icon={Layers}
                        label="MATCH COLLECTION WITH NEED CATEGORY"
                        tooltip="Every discovered collection is listed. Auto-detected categories (from need-metadata:hasCategory) are shown locked; collections without one need a NEED category picked manually."
                        error={collectionsLoading ? undefined : errors.collectionCategories}
                      >
                        <CollectionCategoryPicker
                          collections={collections}
                          loading={collectionsLoading}
                          value={formData.collectionCategories}
                          onChange={handleCollectionCategoriesChange}
                        />
                      </FieldWrapper>
                    )}
                    {missingFieldNames().length > 0 && (
                      <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-5">
                        <p className="text-sm font-black uppercase tracking-widest text-red-700 mb-2">
                          Required fields are missing
                        </p>
                        <ul className="list-disc list-inside text-sm font-semibold text-red-700 space-y-1">
                          {missingFieldNames().map((name) => (
                            <li key={name}>{name}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {currentStep === 1 && (
                  <div className="space-y-14">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                      <FieldWrapper icon={Lock} label="Access Governance" tooltip="Who is authorized to access this?" error={errors.access_right}>
                        <select value={formData.access_right} onChange={(e) => updateField('access_right', e.target.value)} className="w-full p-6 bg-green-50/30 border-2 border-green-50 rounded-[2rem] outline-none focus:border-green-500 focus:bg-white font-bold text-slate-700 transition-all cursor-pointer appearance-none">
                          <option value="">Select Rights...</option>
                          {accessRights.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                        </select>
                      </FieldWrapper>
                      <FieldWrapper
                        icon={Calendar}
                        label="Last Date Modified"
                        tooltip="Last known update to the data."
                        error={errors.modified}
                      >
                        <DatePicker
                          selected={formData.modified ? new Date(formData.modified) : null}
                          onChange={(d) => updateField('modified', d?.toISOString().split('T')[0])}
                          dateFormat="dd.MM.yyyy"
                          className="w-full p-6 bg-green-50/30 border-2 border-green-50 rounded-[2rem] outline-none focus:border-green-500 focus:bg-white font-bold text-slate-700 transition-all w-full"
                          placeholderText="DD.MM.YYYY"
                        />
                      </FieldWrapper>
                    </div>
                    {formData.access_right === "RESTRICTED" && (
                      <FieldWrapper
                        icon={Lock}
                        label="Authorization Server"
                        tooltip="Issuer URL of the Keycloak realm that protects the data, e.g. https://auth.example.org/realms/name"
                        error={errors.authorizationServer}
                      >
                        <AuthorizationServerField
                          value={formData.authorizationServer}
                          onChange={(v) => updateField("authorizationServer", v)}
                          openapiUrl={meta.endpointDescription}
                          inputClassName="w-full p-6 bg-green-50/30 border-2 border-green-50 rounded-[2rem] outline-none focus:border-green-500 focus:bg-white font-bold text-slate-700 transition-all"
                        />
                      </FieldWrapper>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-12 pt-10 border-t border-slate-50">
                      {[
                        { label: "Contains Synthetic Content", key: "containsSyntheticData", tooltip: "Contains synthesised or AI-generated data?" },
                        { label: "Cost-Free Access", key: "isAccessibleWithoutPayment", tooltip: "Can the user access the Dataset free of charge to access?" },
                        { label: "Registration-Free Access", key: "isAccessibleWithoutRegistration", tooltip: "No registration or login needed?" },
                        { label: "Creator Type", key: "hasCreatorType", tooltip: "Is the Dataset creator an official data provider or not?", options: creatorTypes }
                      ].map(field => (
                        <FieldWrapper key={field.key} label={field.label} tooltip={field.tooltip} error={errors[field.key]}>
                          <div className="flex gap-4">
                            {(field.options || ['true', 'false']).map(val => (
                              <button key={val} onClick={() => updateField(field.key, val)} className={`flex-1 py-5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all border ${formData[field.key] === val ? 'bg-green-600 text-white border-green-600 shadow-lg shadow-green-300/70' : 'bg-green-50 text-green-700 border-black/5 shadow-md shadow-green-200/80 hover:shadow-lg hover:shadow-green-300/70 hover:bg-green-100'}`}>
                                {val === 'true' ? 'Yes' : val === 'false' ? 'No' : val}
                              </button>
                            ))}
                          </div>
                        </FieldWrapper>
                      ))}
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-14">
                    <FieldWrapper icon={ShieldCheck} label="EUROPA Data Themes" tooltip="Select all relevant EUROPA standard data sthemes." error={errors.dataThemes}>
                      <DataThemesSection euDataThemes={euDataThemes} selected={formData.dataThemes} onToggle={(code) => {
                        const newThemes = formData.dataThemes.includes(code) ? formData.dataThemes.filter(c => c !== code) : [...formData.dataThemes, code];
                        updateField('dataThemes', newThemes);
                      }} />
                    </FieldWrapper>
                    <div className="pt-10 border-t border-slate-50">
                      <FieldWrapper icon={MapPin} label="Geographic Scope" tooltip="What geographic extent is covered by this Dataset? Administrative: Official govt boundaries (e.g., specific states, districts, municipalities). Custom: Non-standard areas that may span across states, districts or municipalities." error={errors.datasetScope || errors.location || errors.geometry}>
                        <ScopeSection
                          scope={formData.datasetScope}
                          onScopeChange={(e) => updateField('datasetScope', e.target.value)}
                          selectedLocations={selectedLocations}
                          onLocationsChange={setSelectedLocations}
                          selectedStates={selectedStates}
                          onStatesChange={setSelectedStates}
                          geometryValue={formData.geometry}
                          onGeometryChange={(val) => updateField('geometry', val)}
                        />
                      </FieldWrapper>
                    </div>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-8">
                    {needsOrgChoice && (
                      <div
                        ref={orgPickerRef}
                        className={`border-2 p-6 md:p-8 transition-all ${
                          ownerOrgId
                            ? "bg-green-50 border-green-300 shadow-md shadow-green-200/80"
                            : orgChoiceMissing
                              ? "bg-red-50 border-red-500 shadow-lg shadow-red-300/70 ring-4 ring-red-100"
                              : "bg-amber-50 border-amber-400 shadow-lg shadow-amber-300/70"
                        }`}
                      >
                        <div className="flex items-start gap-4 mb-5">
                          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                            ownerOrgId ? "bg-green-600 text-white" : orgChoiceMissing ? "bg-red-600 text-white" : "bg-amber-500 text-white"
                          }`}>
                            {ownerOrgId ? <CheckCircle2 size={22} /> : <AlertTriangle size={22} />}
                          </span>
                          <div>
                            <label htmlFor="owner-org" className="block text-xl font-black tracking-tight text-slate-900">
                              {ownerOrgId ? "Registering for" : "Choose the organisation this datasource belongs to"}
                            </label>
                            <p className="mt-1 text-base font-medium text-slate-700">
                              You belong to several organisations. The one you pick owns this datasource and can modify or deregister it.
                            </p>
                            {orgChoiceMissing && !ownerOrgId && (
                              <p className="mt-2 text-sm font-black text-red-700">
                                Registration can't be submitted until you choose an organisation.
                              </p>
                            )}
                          </div>
                        </div>
                        <select
                          id="owner-org"
                          value={ownerOrgId}
                          onChange={(e) => { setOwnerOrgId(e.target.value); setOrgChoiceMissing(false); }}
                          className={`w-full px-5 py-4 bg-white border-2 rounded-2xl text-lg font-bold text-slate-900 focus:outline-none ${
                            ownerOrgId ? "border-green-400 focus:border-green-600" : orgChoiceMissing ? "border-red-400 focus:border-red-600" : "border-amber-400 focus:border-amber-600"
                          }`}
                        >
                          <option value="" disabled>Choose an organisation…</option>
                          {userOrgs.map((org) => (
                            <option key={org.id} value={org.id}>{org.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="bg-slate-900 rounded-[3rem] p-10 md:p-14 text-white shadow-2xl relative overflow-hidden border border-slate-800">
                      <div className="relative z-10">
                        <div className="flex items-center justify-between border-b border-white/10 pb-8 mb-10">
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-green-500/20 rounded-2xl"><CheckCircle2 className="text-green-400" size={32} /></div>
                            <div>
                              <h3 className="font-black text-2xl tracking-tighter">Registration Summary</h3>
                              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Review all fields before submission</p>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                          <div className="space-y-6">
                            <ReviewItem
                              label="Organisation"
                              value={needsOrgChoice
                                ? userOrgs.find((o) => o.id === ownerOrgId)?.name
                                : userOrgs[0]?.name}
                            />
                            <ReviewItem label="Endpoint URL" value={formData.endpointURL} isLongText />
                            <ReviewItem label="Dataset Title" value={meta.title} isLongText />
                            <ReviewItem label="Datset Description" value={meta.description} />
                            <ReviewItem label="Data Provider" value={meta.provider} />
                            <ReviewItem label="NEED-Category" value={formData.category?.join(", ")} />
                            <ReviewItem label="Access Right" value={accessRights.find(r => r.value === formData.access_right)?.label || formData.access_right} />
                            {formData.access_right === "RESTRICTED" && (
                              <ReviewItem label="Authorization Server" value={formData.authorizationServer} isLongText />
                            )}
                            <ReviewItem label="EU Data Themes" value={formData.dataThemes?.join(", ")} isLongText />
                            <ReviewItem label="Last Modified" value={formData.modified} />
                          </div>
                          <div className="space-y-6">
                            <ReviewItem label="Geographic Scope" value={formData.datasetScope} />
                            <ReviewItem label="Location" value={formData.datasetScope === 'custom' ? selectedStates.map(s => s.name).join(", ") : selectedLocations[selectedLocations.length - 1]?.name} />
                            <ReviewItem label="Contains Synthetic Data" value={formData.containsSyntheticData} />
                            <ReviewItem label="Openapi Specification" value={meta.endpointDescription} />
                            <ReviewItem label="Keywords" value={meta.keywords?.join(", ")} />
                            <ReviewItem label="Cost-Free Access" value={formData.isAccessibleWithoutPayment} />
                            <ReviewItem label="Registration-Free Access" value={formData.isAccessibleWithoutRegistration} />
                            <ReviewItem label="Creator-Type" value={formData.hasCreatorType} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-20 flex justify-between items-center pt-12 border-t border-slate-100">
              <button onClick={handleBack} className={`px-10 py-5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${currentStep === 0 ? "opacity-0 invisible" : "text-slate-400 hover:text-green-600 hover:bg-green-50"}`}>
                <ChevronLeft size={20} className="inline mr-2" /> Previous
              </button>
              {currentStep < 3 ? (
                <button onClick={handleNext} className="group px-14 py-5 bg-green-50 text-green-700 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-green-600 hover:text-white transition-all shadow-xl shadow-green-100 active:scale-95">
                  Continue <ChevronRight size={20} className="inline ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : activeJobId ? (
                <JobEventLog jobId={activeJobId} onTerminal={handleJobTerminal} />
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading || !!activeJobId}
                  className={`relative px-14 py-5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-2xl ${
                    activeJobId
                      ? "bg-slate-800 text-white cursor-wait"
                      : "bg-green-600 text-white hover:bg-green-700 shadow-green-200"
                  }`}
                >
                  {activeJobId ? "Syncing..." : (loading ? "Preparing..." : "Finalize Registration")}
                </button>
              )}
            </div>
          </div>
          <ResultOverlay isVisible={isSuccess} success title="Registration Complete" buttonLabel="Close" onClose={handleSuccessRestart}>
            The dataset has been successfully processed and synchronized with the <CatalogName />.
          </ResultOverlay>
          <ResultOverlay isVisible={!!failureMessage} title="Registration Failed" buttonLabel="Dismiss" onClose={closeFailure}>
            {failureMessage}
          </ResultOverlay>
          <ResumeDraftPrompt
            isVisible={showResumePrompt}
            draftMeta={serverDraft}
            onResume={resumeDraft}
            onDiscard={discardDraft}
          />
          <LeaveConfirmPrompt
            isVisible={showLeavePrompt}
            onCancel={() => setShowLeavePrompt(false)}
            onSaveAndExit={leaveSaveAndExit}
            onDiscardAndExit={leaveDiscardAndExit}
          />
        </div>
      </main>
    </div>
  );
}

const PROMPT = "rounded-[3rem] p-12 max-w-xl";
const PROMPT_BUTTON = "rounded-2xl font-black text-xs uppercase tracking-widest transition-all active:scale-95";

function ResumeDraftPrompt({ isVisible, draftMeta, onResume, onDiscard }) {
  const ts = draftMeta?.ts ? new Date(draftMeta.ts).toLocaleString() : "earlier";
  const title = draftMeta?.meta?.title || draftMeta?.formData?.endpointURL || "(untitled draft)";
  return (
    <Modal isVisible={isVisible} z="z-[120]" className={PROMPT}>
      <DialogHeader icon={Save} tone={DIALOG_TONES.green} title="Resume previous draft?" caption={`Saved ${ts}`} />
      <div className="bg-slate-50 rounded-2xl p-6 mb-8">
        <p className="text-[10px] font-bold text-green-600 uppercase tracking-widest mb-1">Draft for</p>
        <p className="text-sm font-bold text-slate-900 break-words">{title}</p>
      </div>
      <p className="text-sm text-slate-600 mb-8 leading-relaxed">
        You stopped a registration in progress. Resume where you left off, or discard the draft and start fresh.
      </p>
      <div className="flex gap-4">
        <button onClick={onDiscard} className={`flex-1 py-5 bg-slate-100 text-slate-700 hover:bg-slate-200 ${PROMPT_BUTTON}`}>
          Start Fresh
        </button>
        <button onClick={onResume} className={`flex-1 py-5 bg-green-600 text-white hover:bg-green-700 shadow-xl shadow-green-200 ${PROMPT_BUTTON}`}>
          Resume Draft
        </button>
      </div>
    </Modal>
  );
}

function LeaveConfirmPrompt({ isVisible, onCancel, onSaveAndExit, onDiscardAndExit }) {
  return (
    <Modal isVisible={isVisible} z="z-[120]" className={PROMPT}>
      <DialogHeader icon={AlertTriangle} tone={DIALOG_TONES.amber} title="Leave registration?" caption="Your progress is unsaved" />
      <p className="text-sm text-slate-600 mb-8 leading-relaxed">
        You have an in-progress registration. Save it and pick up where you left off next time, or discard
        your changes and return to the home page.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button onClick={onCancel} className={`py-5 bg-white border-2 border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 ${PROMPT_BUTTON}`}>
          <X size={14} className="inline mr-2" /> Cancel
        </button>
        <button onClick={onDiscardAndExit} className={`py-5 bg-red-50 text-red-700 hover:bg-red-600 hover:text-white ${PROMPT_BUTTON}`}>
          Discard
        </button>
        <button onClick={onSaveAndExit} className={`py-5 bg-green-600 text-white hover:bg-green-700 shadow-xl shadow-green-200 ${PROMPT_BUTTON}`}>
          <Save size={14} className="inline mr-2" /> Save & Exit
        </button>
      </div>
    </Modal>
  );
}
